import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { VehicleListing } from '@/models/registry';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListingStatus } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { EyeOff, SearchCheck } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehicleStatusBadge } from './vehicle-status-badge';

export const getStatusOptions = ({
  className,
}: {
  className: string;
}): Option<VehicleListingStatus>[] => {
  return [
    {
      value: 'ACTIVE',
      label: 'Active',
      icon: <SearchCheck className={className} />,
      hotkey: '1',
    },
    {
      value: 'HIDDEN',
      label: 'Hidden',
      icon: <EyeOff className={className} />,
      hotkey: '2',
    },
  ];
};

export const STATUS_OPTIONS_MAP = getStatusOptions({ className: 'h-3 w-3' });

export type VehicleStatusSelectorProps = {
  id: string;
  value: VehicleListing['status'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehicleStatusSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehicleStatusSelectorProps) {
  const statusButtonRef = useRef<HTMLButtonElement>(null);

  const updateStatusMutation = useMutation({
    onMutate: async ({ status }) => {
      vehicleListingStore.updateStatus({ id, status });
    },
    mutationFn: async ({ status }: { status: VehicleListing['status'] }) => {
      return await pdkAxios.post(`/inventory/listings/${id}/status`, {
        status,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleStatusChange = (newStatus: VehicleListing['status']) => {
    updateStatusMutation.mutate({ status: newStatus });
  };

  const currentOption = useMemo(() => {
    return STATUS_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.VEHICLE_LISTING_STATUS}</KeyboardKeyIcon>
        }
        placeholder="Set status..."
        currentOption={currentOption}
        onChange={handleStatusChange}
        emptyMessage="No status found"
        options={STATUS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = STATUS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleStatusChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehicleStatusBadge
            value={value ?? ''}
            hideCopy={hideCopy}
            buttonRef={statusButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_LISTING_STATUS}
          onHotkeyPressed={() => {
            statusButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
